import { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import qs from 'qs'
import snakeCase from 'lodash/snakeCase'
import { scroller as scroll } from 'react-scroll'
import { FormattedMessage } from 'react-intl'
import { useFormatMessage } from 'src/providers/intl'

import { constructFilters } from 'src/helpers/listings/filters'
import { camelizeKeys } from 'src/helpers/utils'

// Actions
import {
  searchListingsForListView,
  fetchSearchListing,
  toggleListingSearchModal,
} from 'src/actions/listings'

import { loadSuggestedLocations, setFilter } from 'src/actions/filters'

// Components
import { AlertToggle } from 'src/Components/homeSearch/Listings/AlertToggle'
import Filters from 'src/Components/homeSearch/Filters'
import Footer from 'src/Components/homeSearch/Footer'
import ListingsMap from 'src/Components/homeSearch/Listings/ListingsMap'
import ListingsSort from 'src/Components/homeSearch/Listings/ListingsSort'
import {
  BotHeadSpinWithMessage,
  Button,
  OverlayPanel,
  Text,
} from 'src/Components'
import OverlayMask from 'src/Components/shared/Routing/OverlayMask'
import PreferencesPanel from 'src/Components/shared/PreferencesPanel'

// Constants
import { OVERLAY_HELPERS } from 'src/constants/navigation'
import {
  LISTINGS_SEARCH,
  LOCATION_LEVELS,
  LOCATION,
  POSTAL_CODE,
  STREET,
  NEIGHBOURHOOD,
  COUNTY,
  CITY,
  STATE,
  MAX_PRICE_CENTS,
  MAX_SQFT,
  MAX_YEAR_BUILT,
  MIN_BATHROOMS_COUNT,
  MIN_BEDROOMS_COUNT,
  MIN_PRICE_CENTS,
  MIN_SQFT,
  MIN_YEAR_BUILT,
  PROPERTY_TYPES,
  ORDER,
  PAGE,
} from 'src/constants/listings'

// Selectors
import { selectCustomerId } from 'src/store/selectors/customerProfile'
import { selectFilter, selectFiltersCount } from 'src/store/selectors/filters'
import {
  selectHomebotListingsSearchLoading,
  selectSearchListings,
  selectSearchListingsTotal,
} from 'src/store/selectors/listings'

// Styles
import SearchIcon from 'src/Components/homeSearch/Search/SearchBar/SearchIcon'
import ListView from './ListView'
import styles from './Listings.module.scss'
import { ListingsEmptyState } from './ListingsEmptyState/ListingsEmptyState'
import { ListViewFilterViewSpacingWrapper } from '../ListViewFilterViewSpacingWrapper/ListViewFilterViewSpacingWrapper'

const Listings = () => {
  const dispatch = useDispatch()
  const intlFormat = useFormatMessage()
  const listings = useSelector(selectSearchListings)
  const listingsTotal = useSelector(selectSearchListingsTotal)
  const filter = useSelector(selectFilter)
  const filtersCount = useSelector(selectFiltersCount)
  const customerId = useSelector(selectCustomerId)
  const [focusedMarker, setFocusedMarker] = useState(null)
  const [focusedListing, setFocusedListing] = useState(null)
  const [showFilters, setShowFilters] = useState(false)
  const isLoadingListings = useSelector(selectHomebotListingsSearchLoading)
  const queryParams = camelizeKeys(
    qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    })
  )

  // splitting apart this array for useEffect dependencies array
  const [propertyType0, propertyType1, propertyType2, propertyType3] =
    queryParams?.[PROPERTY_TYPES] || [
      undefined,
      undefined,
      undefined,
      undefined,
    ]

  useEffect(() => {
    LOCATION_LEVELS.find(currentLocationLevel => {
      const value = queryParams[currentLocationLevel]
      if (value) {
        dispatch(
          loadSuggestedLocations({
            query: `${value} ${snakeCase(currentLocationLevel)}`,
          })
        )
      }
      return value
    })

    const searchFilters = constructFilters(queryParams)
    dispatch(setFilter(searchFilters))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    queryParams?.[POSTAL_CODE],
    queryParams?.[STREET],
    queryParams?.[NEIGHBOURHOOD],
    queryParams?.[COUNTY],
    queryParams?.[CITY],
    queryParams?.[STATE],
    queryParams?.[MIN_PRICE_CENTS],
    queryParams?.[MAX_PRICE_CENTS],
    queryParams?.[MIN_BATHROOMS_COUNT],
    queryParams?.[MIN_BEDROOMS_COUNT],
    queryParams?.[MIN_SQFT],
    queryParams?.[MAX_SQFT],
    queryParams?.[MIN_YEAR_BUILT],
    queryParams?.[MAX_YEAR_BUILT],
    propertyType0,
    propertyType1,
    propertyType2,
    propertyType3,
  ])

  useEffect(() => {
    const { location } = filter

    if (location?.id) {
      dispatch(
        searchListingsForListView(customerId, {
          ...filter,
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    customerId,
    filter,
    filter?.[LOCATION],
    filter?.[ORDER],
    filter?.[PAGE],
    filter?.[MIN_PRICE_CENTS],
    filter?.[MAX_PRICE_CENTS],
    filter?.[MIN_BATHROOMS_COUNT],
    filter?.[MIN_BEDROOMS_COUNT],
    filter?.[MIN_SQFT],
    filter?.[MAX_SQFT],
    filter?.[MIN_YEAR_BUILT],
    filter?.[MAX_YEAR_BUILT],
    filter?.[PROPERTY_TYPES],
  ])

  const onFocus = listing => {
    setFocusedMarker(listing !== null ? listing : focusedListing)
  }

  const onMarkerClick = marker => {
    const listingLoaded = listings.find(listing => listing.id === marker.id)

    scroll.scrollTo(listingLoaded ? `listing_${marker.id}` : 'list-bottom', {
      offset: -200,
      duration: 200,
      delay: 100,
      smooth: 'easeInOutQuart',
    })

    if (!listingLoaded) {
      dispatch(fetchSearchListing(customerId, marker.id, LISTINGS_SEARCH))
    }

    setFocusedMarker(marker)
    setFocusedListing(marker)
  }

  const getListingsListView = () => {
    if (isLoadingListings) {
      return (
        <BotHeadSpinWithMessage className={styles.loadingContainer}>
          <FormattedMessage
            id='BotHeadSpinWithMessage.ListingsListView'
            defaultMessage='Loading homes for you...'
          />
        </BotHeadSpinWithMessage>
      )
    }
    if (!isLoadingListings && (!listings || listings.length === 0)) {
      return <ListingsEmptyState />
    }
    return (
      <ListView
        listings={listings}
        customerId={customerId}
        onFocus={onFocus}
        focusedListing={focusedListing}
      />
    )
  }

  const getListingsTotalDisplay = () => {
    if (!listingsTotal || listingsTotal === 0) {
      return null
    }
    if (listingsTotal === 1) {
      return '1 home found'
    }
    return `${listingsTotal} homes found`
  }

  return (
    <>
      <OverlayPanel
        back={OVERLAY_HELPERS.preferences.getOrigin()}
        path={OVERLAY_HELPERS.preferences.buildPath()}
        title={OVERLAY_HELPERS.getTitle('preferences', intlFormat)}
        disableAnchor
        component={PreferencesPanel}
      />
      <OverlayMask isExact>
        <div className={styles.listingsWrapper}>
          {showFilters ? (
            <Filters close={() => setShowFilters(false)} />
          ) : (
            <ListViewFilterViewSpacingWrapper view='list'>
              <div className={styles.listingsResults}>
                <div className={styles.searchWrapper}>
                  <Button
                    onClick={() => dispatch(toggleListingSearchModal(true))}
                    className={styles.searchBarBtn}
                  >
                    <div className={styles.placeholder}>
                      <SearchIcon className={styles.svg} />
                      <Text
                        className={styles.cta}
                        variant='neutralCool'
                        variantCount='900'
                        element='span'
                      >
                        {filter?.location?.name}
                      </Text>
                    </div>
                  </Button>
                  <AlertToggle />
                </div>
                <div className={styles.countWrapper}>
                  <Text
                    element='h2'
                    size='l'
                    weight='bold'
                    align='left'
                    variant='neutralCool'
                    variantCount='900'
                  >
                    {getListingsTotalDisplay()}
                  </Text>
                  <Button
                    className={styles.filterButton}
                    data-qa='listingsResultsFilterButton'
                    aria-label='filter listings'
                    compact
                    onClick={() => setShowFilters(true)}
                  >
                    <FormattedMessage
                      id='HomeSearch.listings.filters.button'
                      defaultMessage='Filters'
                    />
                    <div className={styles.filtersCount}>{filtersCount}</div>
                  </Button>
                  <ListingsSort />
                </div>
                <div>{getListingsListView()}</div>
                <div>
                  <Footer />
                </div>
              </div>
            </ListViewFilterViewSpacingWrapper>
          )}
          <div className={styles.map}>
            {filter.location?.id && (
              <ListingsMap
                filter={filter}
                listings={listings}
                focusedMarker={focusedMarker}
                onMarkerClick={onMarkerClick}
              />
            )}
          </div>
        </div>
      </OverlayMask>
    </>
  )
}

export default Listings
